import { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Check, ArrowRight, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

export default function DiscoverySection() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const startCheckout = async () => {
        setError("");
        setLoading(true);
        try {
            const { data } = await api.post("/create-checkout-session", {
                package_id: "discovery_session",
                origin_url: window.location.origin,
            });
            if (data?.checkout_url) {
                window.location.href = data.checkout_url;
            } else {
                setError("Unable to start checkout. Please try again.");
                setLoading(false);
            }
        } catch (err) {
            setError(
                formatApiErrorDetail(err?.response?.data?.detail) ||
                    err?.message ||
                    "Unable to start checkout. Please try again."
            );
            setLoading(false);
        }
    };

    const perks = [
        "60-minute private session with a senior EIGOO editor",
        "Personalised review of your creative brief or project",
        "Custom recommendation deck delivered within 48 hours",
        "Priority access to upcoming collector editions",
    ];

    return (
        <section
            id="discovery"
            data-testid="discovery-section"
            className="relative border-y"
            style={{ borderColor: "rgba(212,175,55,0.18)", background: "#080808" }}
        >
            <div className="absolute inset-0 spotlight pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-12 gap-14 items-center">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9 }}
                    className="md:col-span-7"
                >
                    <div className="eyebrow mb-4 flex items-center gap-2">
                        <Sparkles className="w-3.5 h-3.5" /> Book a Discovery Session — $97
                    </div>
                    <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight">
                        Speak with an editor about your{" "}
                        <span className="text-gold-gradient italic">next story</span>.
                    </h2>
                    <p className="text-[#cfcfcf] mt-6 max-w-xl leading-relaxed">
                        A one-time, one-hour session with a senior EIGOO editor. Bring a manuscript, a
                        pitch, or just an idea — we'll help you understand what it needs to become a
                        finished, publishable work. Professional. Focused. Welcoming.
                    </p>

                    <ul className="mt-8 space-y-3 text-sm text-[#e6e6e6]">
                        {perks.map((p) => (
                            <li key={p} className="flex items-start gap-3">
                                <Check className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                                <span>{p}</span>
                            </li>
                        ))}
                    </ul>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, delay: 0.15 }}
                    className="md:col-span-5"
                >
                    <div className="frame-gold p-8 md:p-10">
                        <div className="eyebrow mb-3">Discovery Session</div>
                        <div className="flex items-baseline gap-2">
                            <span className="font-serif text-6xl text-gold-gradient">$97</span>
                            <span className="text-[#A1A1AA] text-sm uppercase tracking-widest">
                                USD · one-time
                            </span>
                        </div>
                        <div className="gold-rule my-6" />
                        <ul className="space-y-2 text-sm text-[#cfcfcf] mb-8">
                            <li>60-minute video call</li>
                            <li>Written recommendation deck</li>
                            <li>Priority collector-edition access</li>
                            <li>Secure checkout via Stripe</li>
                        </ul>
                        <button
                            data-testid="discovery-book-btn"
                            onClick={startCheckout}
                            disabled={loading}
                            className="btn-gold w-full justify-center disabled:opacity-60"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    Redirecting to Stripe…
                                </>
                            ) : (
                                <>
                                    Book Now <ArrowRight className="w-3.5 h-3.5" />
                                </>
                            )}
                        </button>
                        {error && (
                            <p
                                data-testid="discovery-error"
                                className="mt-4 text-xs uppercase tracking-widest text-[#cd6a6a]"
                            >
                                {error}
                            </p>
                        )}
                        <p className="text-[10px] uppercase tracking-widest text-[#5a5a5a] mt-6 text-center">
                            Powered by Stripe · Cards & wallets accepted
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
